<template>
  <div v-if="play" class="salesPlayHeader">
    <!-- ── Identity row ────────────────────────────────────── -->
    <div class="identityRow">
      <span class="statusDot" :class="statusDotClass" :aria-label="play.status" />
      <h1 class="title">{{ play.name }}</h1>
      <span v-if="isFlock" class="strategyBadge">{{ makerLabel }}</span>
      <span v-if="isDeleted" class="deletedBadge">Deleted</span>
      <template v-else>
        <button v-if="!isFlock" class="editNameBtn" type="button" title="Edit name & description" @click="openEditDetails">
          <AppIcon name="pencil" :size="16" />
        </button>
        <button class="deleteIconBtn" type="button" title="Delete play" :disabled="deleteBusy" @click="onDeleteClick">
          <AppIcon name="trash" :size="16" />
        </button>
      </template>
    </div>

    <div v-if="play.created_by || play.description" class="meta" :class="{ metaClamped: !descriptionExpanded }">
      <span v-if="play.created_by" class="creatorName" :title="play.created_by.email">{{ play.created_by.name }}</span>
      <span
        v-if="play.description"
        ref="descriptionRef"
        class="description"
        :class="{ descriptionClamped: !descriptionExpanded }"
      >{{ play.description }}</span>
      <button
        v-if="play.description && descriptionOverflows"
        class="descriptionToggle"
        type="button"
        @click="descriptionExpanded = !descriptionExpanded"
      >{{ descriptionExpanded ? 'Show less' : 'Show more' }}</button>
    </div>

    <!-- ── Stage bars + activity chips ─────────────────────── -->
    <div class="stageRow">
      <div v-for="bar in stageBars" :key="bar.kind" class="stageBar">
        <span class="stageBarIcon"><AppIcon :name="bar.icon" :size="14" /></span>
        <button
          v-for="seg in bar.segments"
          :key="seg.key"
          class="stageBarSeg"
          :class="{
            stageBarSegActive: seg.key === activeStage && bar.kind === activeKind,
            stageBarSegEmpty: seg.count === 0,
          }"
          type="button"
          @click="onStageClick(seg.key, bar.kind)"
        >
          <span class="stageBarSegLabel">{{ seg.label }}</span>
          <span v-if="seg.count > 0" class="stageBarSegCount">{{ seg.count }}</span>
        </button>
      </div>

      <span v-if="agentRunCounts.running > 0 || agentRunCounts.queued > 0" class="activityChip" title="Agent runs (running / queued)">
        <span v-if="agentRunCounts.running > 0" class="activityPart">
          <span class="dot dotRunning" aria-hidden="true" />
          <span class="activityCount">{{ agentRunCounts.running }}</span>
        </span>
        <span v-if="agentRunCounts.queued > 0" class="activityPart">
          <span class="dot dotQueued" aria-hidden="true" />
          <span class="activityCount">{{ agentRunCounts.queued }}</span>
        </span>
      </span>
      <button
        v-if="pendingReachouts > 0"
        class="n-pill"
        type="button"
        title="Approvals waiting"
        @click="router.push(`/actions?play=${play.id}`)"
      >
        <span class="n-pillDot pillDotDanger" />
        <span class="pillCount">{{ pendingReachouts }} {{ pendingReachouts === 1 ? 'approval' : 'approvals' }}</span>
      </button>
      <button
        v-if="pendingHumanDials > 0"
        class="n-pill"
        type="button"
        title="Calls waiting to be dialed"
        @click="router.push(`/dialer/human?play=${play.id}`)"
      >
        <span class="n-pillDot pillDotCall" />
        <span class="pillCount">{{ pendingHumanDials }} {{ pendingHumanDials === 1 ? 'call' : 'calls' }}</span>
      </button>
    </div>
  </div>

  <!-- ── Modals (outside the flex container to avoid gap) ── -->

    <!-- Edit name & description -->
    <Modal v-if="editDetailsOpen" title="Edit play" @close="editDetailsOpen = false" :max-width="480">
      <div class="editDetailsForm">
        <label class="editDetailsField">
          <span>Name</span>
          <input type="text" v-model="editDetailsDraft.name" placeholder="Play name" @keydown.enter="saveEditDetails" />
        </label>
        <label class="editDetailsField">
          <span>Description</span>
          <textarea v-model="editDetailsDraft.description" placeholder="What does this play do?" rows="3" />
        </label>
        <p class="editDetailsError" v-if="editDetailsError">{{ editDetailsError }}</p>
      </div>
      <template #footer>
        <button class="btn secondary" @click="editDetailsOpen = false">Cancel</button>
        <button class="btn primary" :disabled="editDetailsBusy || !editDetailsDraft.name.trim()" @click="saveEditDetails">
          {{ editDetailsBusy ? 'Saving…' : 'Save' }}
        </button>
      </template>
    </Modal>

    <!-- Delete confirm -->
    <Modal v-if="deleteConfirm" title="Delete play" :max-width="480" @close="deleteConfirm = null">
      <p class="deleteMsg">
        This play will be <strong>permanently deleted</strong>. This cannot be undone.
      </p>
      <ul class="deleteImpacts" v-if="deleteImpacts.length">
        <li v-for="impact in deleteImpacts" :key="impact">{{ impact }}</li>
      </ul>
      <template #footer>
        <button class="btn secondary" @click="deleteConfirm = null">Cancel</button>
        <button class="btn danger" :disabled="deleteBusy" @click="onDeleteConfirm">
          {{ deleteBusy ? 'Deleting…' : 'Delete play' }}
        </button>
      </template>
    </Modal>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch, type Ref } from 'vue'
import { useRouter } from 'vue-router'
import AppIcon from '../../components/AppIcon.vue'
import Modal from './Modal.vue'
import { useSalesPlayActions } from '../composables/useSalesPlayActions'
import { strategyMakerLabel, usePlayEngine, type PlayEngine } from '../composables/usePlayEngine'
import { useCurrentUser } from '../../services/user'

interface PlayData {
  id: string
  name: string
  description: string | null
  status: string
  engine?: PlayEngine
  made_by?: 'user' | 'strategy' | null
  created_by: { id: string; email: string; name: string } | null
  pending_review_count?: number
  staged_account_count?: number
  staged_contact_count?: number
  qualified_count?: number
  qualified_account_count?: number
  qualified_contact_count?: number
  active_count?: number
  enrolled_account_count?: number
  enrolled_contact_count?: number
  engaged_count?: number
  engaged_account_count?: number
  engaged_contact_count?: number
  timed_out_count?: number
  timed_out_account_count?: number
  timed_out_contact_count?: number
  disqualified_count?: number
  disqualified_account_count?: number
  disqualified_contact_count?: number
  pending_reachouts_count?: number
  pending_human_dials_count?: number
}

const props = defineProps<{
  playId: string
  play: PlayData
  agentRunCounts: { running: number; queued: number }
  activeStage?: string | null
  activeKind?: 'account' | 'contact' | null
  refreshPlay: () => Promise<void>
}>()

const emit = defineEmits<{
  (e: 'stage-filter', stage: string | null, kind: 'account' | 'contact'): void
}>()

const router = useRouter()

// Use the shared composable for all action handlers + modal state.
// The composable expects Refs, so wrap the props.
import { toRef } from 'vue'
const playIdRef = toRef(props, 'playId')
const playRef = toRef(props, 'play') as Ref<any>

const {
  deleteBusy, deleteConfirm, onDeleteClick, onDeleteConfirm,
  editDetailsOpen, editDetailsBusy, editDetailsError, editDetailsDraft, openEditDetails, saveEditDetails,
} = useSalesPlayActions(playIdRef, playRef, () => props.refreshPlay())

function plural(n: number, singular: string, plural_: string): string {
  return `${n} ${n === 1 ? singular : plural_}`
}

const deleteImpacts = computed<string[]>(() => {
  const preview = deleteConfirm.value
  if (!preview) return []
  const lines: string[] = []
  const members: string[] = []
  if (preview.active_accounts) members.push(plural(preview.active_accounts, 'account', 'accounts'))
  if (preview.active_contacts) members.push(plural(preview.active_contacts, 'contact', 'contacts'))
  if (members.length) lines.push(`${members.join(' and ')} will leave the play`)
  if (preview.pending_reachout_count) {
    lines.push(`${plural(preview.pending_reachout_count, 'unsent reachout', 'unsent reachouts')} will be removed from your queue`)
  }
  if (preview.dialer_entry_count) {
    lines.push(`${plural(preview.dialer_entry_count, 'dialer queue entry', 'dialer queue entries')} will be removed`)
  }
  return lines
})


const isDeleted = computed(() => String(props.play?.status || '').toLowerCase() === 'archived')
const { isFlock } = usePlayEngine(toRef(props, 'play'))
const { me, loadCurrentUser } = useCurrentUser()
onMounted(() => { void loadCurrentUser() })
const makerLabel = computed(() => (props.play ? strategyMakerLabel(props.play, (me.value as any)?.user_id) : ''))

const statusDotClass = computed(() => {
  const s = String(props.play?.status || '').toLowerCase()
  if (s === 'archived') return 'statusDotDeleted'
  return s === 'active' ? 'statusDotActive' : 'statusDotDisabled'
})

const pendingReachouts = computed(() => props.play?.pending_reachouts_count || 0)
const pendingHumanDials = computed(() => props.play?.pending_human_dials_count || 0)

// ── Description show more/less ───────────────────────────────────────
// Detects real overflow (rather than guessing from character count) so the
// toggle only appears when the clamped single line actually truncates.
const descriptionRef = ref<HTMLElement | null>(null)
const descriptionExpanded = ref(false)
const descriptionOverflows = ref(false)

function checkDescriptionOverflow() {
  const el = descriptionRef.value
  descriptionOverflows.value = !!el && el.scrollWidth > el.clientWidth
}

watch(
  () => props.play?.description,
  () => {
    descriptionExpanded.value = false
    void nextTick(checkDescriptionOverflow)
  },
  { immediate: true },
)

onMounted(() => window.addEventListener('resize', checkDescriptionOverflow))
onBeforeUnmount(() => window.removeEventListener('resize', checkDescriptionOverflow))

const stageBars = computed(() => {
  const p = props.play
  return [
    {
      kind: 'account' as const,
      icon: 'account',
      segments: [
        { key: 'staged', label: 'Staged', count: p?.staged_account_count || 0 },
        { key: 'qualified', label: 'Qualified', count: p?.qualified_account_count || 0 },
        { key: 'active', label: 'Active', count: p?.enrolled_account_count || 0 },
        { key: 'disqualified', label: 'Disqualified', count: p?.disqualified_account_count || 0 },
      ],
    },
    {
      kind: 'contact' as const,
      icon: 'contact',
      segments: [
        { key: 'staged', label: 'Staged', count: p?.staged_contact_count || 0 },
        { key: 'qualified', label: 'Qualified', count: p?.qualified_contact_count || 0 },
        { key: 'active', label: 'Active', count: p?.enrolled_contact_count || 0 },
        { key: 'engaged', label: 'Engaged', count: p?.engaged_contact_count || 0 },
        { key: 'timed_out', label: 'Timed Out', count: p?.timed_out_contact_count || 0 },
        { key: 'disqualified', label: 'Disqualified', count: p?.disqualified_contact_count || 0 },
      ],
    },
  ]
})

function onStageClick(stage: string, kind: 'account' | 'contact') {
  if (props.activeStage === stage && props.activeKind === kind) {
    emit('stage-filter', null, kind)
  } else {
    emit('stage-filter', stage, kind)
  }
}
</script>

<style scoped>
.salesPlayHeader {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  margin-bottom: var(--space-5);
}

/* ── Identity row ────────────────────────────────────────── */
.identityRow {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}
.statusDot {
  width: var(--space-2);
  height: var(--space-2);
  border-radius: var(--radius-full);
  flex-shrink: 0;
}
.statusDotActive {
  background: var(--color-accent);
  box-shadow: 0 0 0 var(--space-1) var(--color-accent-soft);
}
.statusDotDisabled { background: var(--color-text-tertiary); }
.statusDotDeleted { background: var(--color-danger, #b91c1c); }
.strategyBadge {
  padding: 2px var(--space-2);
  border-radius: var(--radius-sm);
  background: var(--color-accent-soft);
  color: var(--color-text-primary);
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
}
.deletedBadge {
  padding: 2px var(--space-2);
  border-radius: var(--radius-sm);
  background: var(--color-danger, #b91c1c);
  color: #fff;
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  text-transform: uppercase;
  letter-spacing: var(--tracking-wide);
}
.title {
  margin: 0;
  font-family: var(--font-display);
  font-size: var(--fs-xl);
  font-weight: var(--fw-semibold);
  letter-spacing: var(--tracking-tight);
  line-height: var(--lh-tight);
  color: var(--color-text-primary);
}
.editNameBtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: var(--space-1);
  cursor: pointer;
  color: var(--color-text-tertiary);
  border-radius: var(--radius-sm);
  transition: color var(--duration-fast) var(--ease-out), background var(--duration-fast) var(--ease-out);
}
.editNameBtn:hover {
  color: var(--color-text-primary);
  background: var(--color-bg-subtle);
}
.deleteIconBtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: var(--space-1);
  cursor: pointer;
  color: var(--color-text-tertiary);
  border-radius: var(--radius-sm);
  transition: color var(--duration-fast) var(--ease-out), background var(--duration-fast) var(--ease-out);
}
.deleteIconBtn:hover {
  color: var(--color-danger, #b91c1c);
  background: var(--color-bg-subtle);
}
.deleteIconBtn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ── Meta ─────────────────────────────────────────────────── */
.meta {
  margin: 0;
  font-family: var(--font-sans);
  font-size: var(--fs-base);
  line-height: var(--lh-loose);
  color: var(--color-text-secondary);
}
/* Collapsed: description truncates to one line, sharing that line with the
   creator name and toggle — flex is what lets it shrink instead of wrapping
   or overflowing past its siblings. Expanded: back to normal block flow so
   the full text wraps across the page like ordinary paragraph text. */
.meta.metaClamped {
  display: flex;
  align-items: baseline;
  gap: var(--space-2);
}
.creatorName {
  font-weight: var(--fw-semibold);
  color: var(--color-text-primary);
  flex-shrink: 0;
}
.meta:not(.metaClamped) .creatorName {
  margin-inline-end: var(--space-2);
}
.description { color: var(--color-text-secondary); }
.metaClamped .description {
  flex-shrink: 1;
  min-width: 0;
}
.description.descriptionClamped {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  vertical-align: bottom;
}
.descriptionToggle {
  flex-shrink: 0;
  padding: 0;
  border: none;
  background: none;
  font-family: var(--font-sans);
  font-size: var(--fs-base);
  font-weight: var(--fw-semibold);
  color: var(--color-accent);
  cursor: pointer;
  white-space: nowrap;
}
.meta:not(.metaClamped) .descriptionToggle {
  margin-inline-start: var(--space-2);
}
.descriptionToggle:hover { text-decoration: underline; }

/* ── Stage + activity chips row ──────────────────────────── */
.stageRow {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  flex-wrap: wrap;
}
.stageBar {
  display: inline-flex;
  align-items: stretch;
  height: 28px;
  background: var(--color-bg-surface);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-full);
  overflow: hidden;
}
.stageBarIcon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 var(--space-2) 0 var(--space-3);
  color: var(--color-text-tertiary);
}
.stageBarSeg {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: 0 var(--space-3);
  background: none;
  border: none;
  border-left: 1px solid var(--color-border-subtle);
  color: var(--color-text-secondary);
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  line-height: 1;
  cursor: pointer;
  transition: color var(--duration-fast) var(--ease-out), background var(--duration-fast) var(--ease-out);
}
.stageBarSeg:hover { color: var(--color-text-primary); background: var(--color-bg-subtle); }
.stageBarSegActive { color: var(--color-text-primary); background: var(--color-bg-subtle, #f2ede5); }
.stageBarSegActive:hover { color: var(--color-text-primary); }
.stageBarSegCount { font-variant-numeric: tabular-nums; }
.stageBarSegEmpty { color: var(--color-text-tertiary); font-weight: var(--fw-medium); }
.stageBarSegEmpty:hover { color: var(--color-text-secondary); }
.activityChip {
  display: inline-flex; align-items: center; gap: var(--space-1); height: 24px; padding: 0 var(--space-2);
  background: var(--color-bg-surface); border: 1px solid var(--color-border-subtle); border-radius: var(--radius-full);
  color: var(--color-text-secondary); font-family: var(--font-sans); font-size: var(--fs-xs); font-weight: var(--fw-semibold); line-height: 1; cursor: default;
}
.activityPart { display: inline-flex; align-items: center; gap: 4px; }
.activityCount { font-variant-numeric: tabular-nums; }
.pillDotDanger { background: var(--color-danger); }
.pillDotCall { background: #E8A33D; }
.pillCount { font-variant-numeric: tabular-nums; }
.dot { width: 6px; height: 6px; border-radius: var(--radius-full); flex-shrink: 0; }
.dotRunning { background: var(--color-accent); box-shadow: 0 0 0 2px var(--color-accent-soft); }
.dotQueued { background: var(--color-text-tertiary); }

/* ── Modal content styles ────────────────────────────────── */
.deleteMsg { margin: 0; font-family: var(--font-sans); font-size: var(--fs-sm); color: var(--color-text-secondary); line-height: var(--lh-normal); }
.deleteImpacts { margin: var(--space-3) 0 0; padding-left: var(--space-4); font-family: var(--font-sans); font-size: var(--fs-sm); color: var(--color-text-secondary); line-height: var(--lh-normal); }
.deleteImpacts li { margin-top: var(--space-1); }
.editDetailsForm { display: flex; flex-direction: column; gap: var(--space-3); }
.editDetailsField { display: flex; flex-direction: column; gap: var(--space-1); }
.editDetailsField > span { font-family: var(--font-sans); font-size: var(--fs-xs); font-weight: var(--fw-semibold); text-transform: uppercase; letter-spacing: var(--tracking-wide); color: var(--color-text-secondary); }
.editDetailsField input, .editDetailsField textarea { padding: var(--space-2); border: 1px solid var(--color-border); border-radius: var(--radius-sm); font-family: var(--font-sans); font-size: var(--fs-sm); }
.editDetailsError { margin: 0; font-family: var(--font-sans); font-size: var(--fs-sm); color: var(--color-danger); }
</style>
